import { apiClient } from './api';

// 10 values total (VP-SALES-006, migration 044): the original 4 plus 6 new
// task-oriented kinds. 'meeting'/'note' are kept even though not in the
// spec's list of 8, since existing rows may already use them.
export type ActivityKind =
  | 'call'
  | 'visit'
  | 'meeting'
  | 'note'
  | 'follow_up'
  | 'pricing'
  | 'collection'
  | 'new_account'
  | 'display'
  | 'other';

export type ActivityPriority = 'low' | 'medium' | 'high';

export interface SalesActivity {
  id: number;
  sales_rep_id: number;
  sales_rep_name: string | null;
  customer_id: number | null;
  customer_name: string | null;
  kind: ActivityKind;
  subject: string;
  notes: string | null;
  outcome: string | null;
  occurred_at: string;
  follow_up_at: string | null;
  priority: ActivityPriority;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivitySummary {
  total: number;
  by_kind: Record<ActivityKind, number>;
  upcoming_followups: number;
}

export interface ListActivitiesFilters {
  page?: number;
  limit?: number;
  sales_rep_id?: number;
  customer_id?: number;
  kind?: ActivityKind;
  from?: string;
  to?: string;
}

export interface ListActivitiesResult {
  data: SalesActivity[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export interface CreateActivityInput {
  sales_rep_id?: number;
  customer_id?: number | null;
  kind: ActivityKind;
  subject: string;
  notes?: string | null;
  outcome?: string | null;
  occurred_at?: string;
  follow_up_at?: string | null;
  priority?: ActivityPriority;
  completed_at?: string | null;
}

export async function listActivities(filters: ListActivitiesFilters = {}): Promise<ListActivitiesResult> {
  return apiClient.get<ListActivitiesResult>('/sales-activities', filters as Record<string, any>);
}

export async function getActivity(id: number): Promise<SalesActivity> {
  return apiClient.get<SalesActivity>(`/sales-activities/${id}`);
}

export async function createActivity(input: CreateActivityInput): Promise<SalesActivity> {
  return apiClient.post<SalesActivity>('/sales-activities', input);
}

export async function updateActivity(id: number, input: Partial<CreateActivityInput>): Promise<SalesActivity> {
  return apiClient.patch<SalesActivity>(`/sales-activities/${id}`, input);
}

export async function deleteActivity(id: number): Promise<void> {
  await apiClient.delete<void>(`/sales-activities/${id}`);
}

export async function getActivitySummary(days: number = 30): Promise<ActivitySummary> {
  return apiClient.get<ActivitySummary>('/sales-activities/summary', { days });
}
